<template>
  <div class="add-product">
    <div class="page-header">
      <div>
        <h2 class="page-title">{{ isEditMode ? '编辑商品' : '添加商品' }}</h2>
        <p class="page-desc">完善商品信息，支持多规格、服务特色等配置</p>
      </div>
      <div class="header-actions">
        <el-button @click="$router.back()">
          <el-icon>
            <Back />
          </el-icon> 返回
        </el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">
          <el-icon>
            <Check />
          </el-icon> 保存商品
        </el-button>
      </div>
    </div>

    <div class="form-container">
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="100px">
        <!-- 基础信息 -->
        <div class="form-section">
          <h3 class="section-title">基础信息</h3>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="商品名称" prop="product_name" required>
                <el-input v-model="formData.product_name" placeholder="请输入商品名称" maxlength="100" show-word-limit />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="副标题">
                <el-input v-model="formData.sub_title" placeholder="请输入副标题" maxlength="200" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="8">
              <el-form-item label="商品分类">
                <el-select v-model="formData.category_id" placeholder="请选择分类" clearable style="width: 100%">
                  <el-option v-for="cat in categories" :key="cat.id" :label="cat.category_name" :value="cat.id" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="8">
              <el-form-item label="商品编号">
                <el-input v-model="formData.product_no" placeholder="自动生成" disabled />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="8">
              <el-form-item label="排序权重">
                <el-input-number v-model="formData.sort_order" :min="0" :max="999" controls-position="right"
                  style="width: 100%" />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- 价格库存 -->
        <div class="form-section">
          <h3 class="section-title">价格库存</h3>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="8">
              <el-form-item label="原价">
                <el-input-number v-model="formData.original_price" :min="0" :precision="2" :step="10"
                  controls-position="right" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="8">
              <el-form-item label="售价" prop="sale_price" required>
                <el-input-number v-model="formData.sale_price" :min="0" :precision="2" :step="10"
                  controls-position="right" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="8">
              <el-form-item label="折扣">
                <el-input-number v-model="formData.discount" :min="0" :max="10" :precision="1" :step="0.5"
                  controls-position="right" style="width: 100%" placeholder="自动计算" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :sm="8">
              <el-form-item label="库存">
                <el-input-number v-model="formData.stock" :min="-1" :step="10" controls-position="right"
                  style="width: 100%">
                  <template #append v-if="formData.stock === -1">不限</template>
                </el-input-number>
                <div class="form-tip">设置为-1表示不限库存</div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="8">
              <el-form-item label="虚拟销量">
                <el-input-number v-model="formData.fictitious_sold" :min="0" :step="100" controls-position="right"
                  style="width: 100%" />
                <div class="form-tip">用于展示，增加商品热度</div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="8">
              <el-form-item label="商品状态">
                <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" active-text="上架"
                  inactive-text="下架" />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- 有效期 -->
        <div class="form-section">
          <h3 class="section-title">有效期设置</h3>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12">
              <el-form-item label="开始日期">
                <el-date-picker v-model="formData.valid_start_date" type="date" placeholder="选择开始日期" style="width: 100%"
                  value-format="YYYY-MM-DD" />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12">
              <el-form-item label="结束日期">
                <el-date-picker v-model="formData.valid_end_date" type="date" placeholder="选择结束日期" style="width: 100%"
                  value-format="YYYY-MM-DD" />
              </el-form-item>
            </el-col>
          </el-row>
          <div class="form-tip">不设置日期表示长期有效</div>
        </div>

        <!-- 商品图片 -->
        <div class="form-section">
          <h3 class="section-title">商品图片</h3>
          <el-form-item label="封面图">
            <el-upload :file-list="coverFiles" action="#" :limit="1" accept="image/*" :auto-upload="false"
              :on-change="handleCoverChange" :on-remove="() => formData.cover_image = ''" list-type="picture-card">
              <el-icon>
                <Plus />
              </el-icon>
              <template #tip>
                <div class="upload-tip">建议尺寸800x800px，支持JPG/PNG</div>
              </template>
            </el-upload>
          </el-form-item>

          <el-form-item label="商品图集">
            <el-upload :file-list="imageFiles" action="#" :limit="5" accept="image/*" :auto-upload="false"
              :on-change="handleImagesChange" :on-remove="handleImagesRemove" list-type="picture-card" multiple>
              <el-icon>
                <Plus />
              </el-icon>
              <template #tip>
                <div class="upload-tip">最多上传5张，建议尺寸800x800px</div>
              </template>
            </el-upload>
          </el-form-item>
        </div>

        <!-- 服务特色 -->
        <div class="form-section">
          <h3 class="section-title">服务特色</h3>
          <el-form-item label="服务标签">
            <el-select v-model="formData.service_features" multiple filterable allow-create default-first-option
              placeholder="请选择或输入服务特色" style="width: 100%">
              <el-option v-for="item in serviceOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>

          <el-form-item label="使用规则">
            <el-input v-model="formData.use_rules_text" type="textarea" :rows="4"
              placeholder="请填写使用规则，如：需提前预约、仅限堂食、不可与其他优惠同享等" />
          </el-form-item>
        </div>

        <!-- 商品详情 -->
        <div class="form-section">
          <h3 class="section-title">商品详情</h3>
          <el-form-item label="简短描述">
            <el-input v-model="formData.description" type="textarea" :rows="3" placeholder="请输入商品简短描述" maxlength="500"
              show-word-limit />
          </el-form-item>

          <el-form-item label="富文本详情">
            <div class="editor-wrapper">
              <QuillEditor ref="quillRef" v-model:content="formData.goods_details" contentType="html"
                :options="editorOptions" style="height: 400px" />
            </div>
          </el-form-item>
        </div>

        <!-- 在商品详情后面添加 -->
        <div class="form-section">
          <h3 class="section-title">商品规格</h3>
          <el-form-item label="规格管理">
            <div class="spec-management">
              <div class="spec-header">
                <el-button type="primary" link @click="openSpecDialog">
                  <el-icon>
                    <Plus />
                  </el-icon> 添加规格
                </el-button>
              </div>
              <div class="spec-list" v-if="specList.length">
                <div class="spec-item" v-for="spec in specList" :key="spec.id">
                  <div class="spec-info">
                    <span class="spec-name">{{ spec.item_name }}</span>
                    <span class="spec-price">¥{{ spec.price }}</span>
                    <span class="spec-quantity">{{ spec.quantity }}{{ spec.unit }}</span>
                  </div>
                  <div class="spec-actions">
                    <el-button type="primary" link size="small" @click="editSpec(spec)">
                      <el-icon>
                        <Edit />
                      </el-icon>
                    </el-button>
                    <el-button type="danger" link size="small" @click="deleteSpec(spec.id)">
                      <el-icon>
                        <Delete />
                      </el-icon>
                    </el-button>
                  </div>
                </div>
              </div>
              <div v-else class="spec-empty">
                <span>暂无规格，点击上方按钮添加</span>
              </div>
            </div>
          </el-form-item>
        </div>

        <!-- 规格弹窗 -->
        <el-dialog v-model="specDialogVisible" title="添加规格" width="550px">
          <el-form :model="specForm" label-width="80px">
            <el-form-item label="规格名称" required>
              <el-input v-model="specForm.item_name" placeholder="如：微辣、大份、加蛋" />
            </el-form-item>

            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="规格类型">
                  <el-select v-model="specForm.item_type" style="width: 100%">
                    <el-option label="菜品" value="dish" />
                    <el-option label="服务" value="service" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="分类">
                  <el-input v-model="specForm.category" placeholder="如：口味、尺寸、配料" />
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="价格">
                  <el-input-number v-model="specForm.price" :min="0" :precision="2" style="width: 100%" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="数量">
                  <el-input-number v-model="specForm.quantity" :min="1" style="width: 100%" />
                </el-form-item>
              </el-col>
            </el-row>

            <el-form-item label="单位">
              <el-input v-model="specForm.unit" placeholder="份/个/杯" style="width: 120px" />
            </el-form-item>

            <el-form-item label="排序">
              <el-input-number v-model="specForm.sort_order" :min="0" :max="999" style="width: 100%" />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="specDialogVisible = false">取消</el-button>
            <el-button type="primary" @click="saveSpec">确 定</el-button>
          </template>
        </el-dialog>
      </el-form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { Plus, Back, Check } from '@element-plus/icons-vue'

export default {
  name: 'AddProductPage',
  components: { QuillEditor, Plus, Back, Check },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const agentId = ref(userInfo.agent_id || 0)
    const storeId = ref(userInfo.id)

    const submitting = ref(false)
    const formRef = ref(null)
    const quillRef = ref(null)
    const isEditMode = ref(false)
    const productId = ref(null)
    const categories = ref([])

    // 文件列表
    const coverFiles = ref([])
    const imageFiles = ref([])

    // 服务选项
    const serviceOptions = ['随时退', '过期自动退', '需预约', '免预约', '到店自提', '全国包邮']

    // 在 setup() 函数中添加
    const specList = ref([])
    const specDialogVisible = ref(false)
    // 在 setup() 函数中，修改 specForm 定义
    const specForm = reactive({
      id: null,
      item_name: '',
      item_type: 'dish',      // 新增：规格类型
      category: '',           // 新增：分类
      price: 0,
      quantity: 1,
      unit: '份',
      sort_order: 0           // 新增：排序
    })

    // 表单数据
    const formData = reactive({
      product_name: '',
      sub_title: '',
      category_id: null,
      category_name: '',
      product_no: '',
      original_price: 0,
      sale_price: 0,
      discount: null,
      stock: -1,
      fictitious_sold: 0,
      cover_image: '',
      images: [],
      service_features: [],
      use_rules_text: '',
      description: '',
      goods_details: '',
      valid_start_date: '',
      valid_end_date: '',
      status: 1,
      sort_order: 0
    })

    // 表单验证规则
    const formRules = {
      product_name: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
      sale_price: [{ required: true, message: '请输入售价', trigger: 'blur' }]
    }

    // 自定义图片上传函数
    const uploadImageToOSS = (file) => {
      return new Promise((resolve, reject) => {
        // 检查文件类型
        if (!file.type.startsWith('image/')) {
          reject(new Error('只能上传图片文件'))
          return
        }
        // 检查文件大小 (5MB)
        if (file.size / 1024 / 1024 > 5) {
          reject(new Error('图片大小不能超过 5MB'))
          return
        }

        getUploadSignature('image')
          .then(signature => uploadToOSS(file, signature))
          .then(ossRes => resolve(ossRes.url))
          .catch(error => {
            console.error('上传失败:', error)
            // 直接传递错误信息，不要包装
            reject(error)
          })
      })
    }

    // 富文本编辑器配置 - 自定义图片上传
    const editorOptions = {
      modules: {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ header: 1 }, { header: 2 }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ direction: 'rtl' }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }],
            [{ font: [] }],
            [{ align: [] }],
            ['clean'],
            ['link', 'image', 'video']
          ],
          handlers: {
            // 自定义图片上传处理
            image: function () {
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', 'image/*')
              input.click()

              input.onchange = async () => {
                const file = input.files[0]
                if (!file) return

                // 显示加载提示
                const loading = ElLoading.service({
                  text: '上传图片中...',
                  background: 'rgba(0, 0, 0, 0.7)'
                })

                try {
                  // 获取当前光标位置
                  const range = this.quill.getSelection(true)

                  // 上传到OSS
                  const url = await uploadImageToOSS(file)

                  // 插入图片到编辑器
                  this.quill.insertEmbed(range.index, 'image', url)

                  // 移动光标到图片后面
                  this.quill.setSelection(range.index + 1)

                  ElMessage.success('图片上传成功')
                } catch (error) {
                  console.error('上传失败:', error)
                  ElMessage.error(error.message || '图片上传失败')
                } finally {
                  loading.close()
                }
              }
            }
          }
        }
      },
      placeholder: '请输入商品详情...',
      theme: 'snow'
    }

    // 生成商品编号
    const generateProductNo = () => {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
      return `P${year}${month}${day}${random}`
    }

    // 获取分类列表
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/category_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, type: 'product' }
        })
        if (response.data.code === 200) {
          categories.value = response.data.data
        }
      } catch (error) {
        console.error('获取分类失败:', error)
      }
    }

    // 获取商品详情（编辑模式）
    const fetchProductDetail = async () => {
      if (!productId.value) return

      const loadingInstance = ElLoading.service({ text: '加载中...' })
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/product_api.php`, {
          params: { agent_id: agentId.value, store_id: storeId.value, id: productId.value }
        })

        if (response.data.code === 200 && response.data.data) {
          const product = response.data.data

          // 解析 JSON 字段
          let images = []
          if (product.images) {
            try {
              images = typeof product.images === 'string' ? JSON.parse(product.images) : product.images
            } catch (e) {
              images = []
            }
          }

          let serviceFeatures = []
          if (product.service_features) {
            try {
              serviceFeatures = typeof product.service_features === 'string' ? JSON.parse(product.service_features) : product.service_features
            } catch (e) {
              serviceFeatures = []
            }
          }

          let useRulesText = ''
          if (product.use_rules) {
            try {
              let useRules = typeof product.use_rules === 'string' ? JSON.parse(product.use_rules) : product.use_rules
              if (useRules.value !== undefined) {
                useRulesText = String(useRules.value)
              } else if (useRules.text) {
                useRulesText = useRules.text
              } else {
                useRulesText = JSON.stringify(useRules, null, 2)
              }
            } catch (e) {
              useRulesText = String(product.use_rules)
            }
          }

          Object.assign(formData, {
            product_name: product.product_name || '',
            sub_title: product.sub_title || '',
            category_id: product.category_id || null,
            category_name: product.category_name || '',
            product_no: product.product_no || '',
            original_price: parseFloat(product.original_price) || 0,
            sale_price: parseFloat(product.sale_price) || 0,
            discount: product.discount,
            stock: product.stock !== undefined ? product.stock : -1,
            fictitious_sold: product.fictitious_sold || 0,
            cover_image: product.cover_image || '',
            images: images,
            service_features: serviceFeatures,
            use_rules_text: useRulesText,
            description: product.description || '',
            goods_details: product.goods_details || '',
            valid_start_date: product.valid_start_date || '',
            valid_end_date: product.valid_end_date || '',
            status: product.status !== undefined ? product.status : 1,
            sort_order: product.sort_order || 0
          })

          // 设置文件列表
          if (product.cover_image) {
            coverFiles.value = [{ uid: Date.now(), name: 'cover', url: product.cover_image }]
          } else {
            coverFiles.value = []
          }

          if (images.length > 0) {
            imageFiles.value = images.map((url, idx) => ({ uid: Date.now() + idx, name: `image-${idx}`, url }))
          } else {
            imageFiles.value = []
          }

          // ========== 新增：加载商品规格列表 ==========
          await fetchSpecList()

          ElMessage.success('商品信息加载成功')
        } else {
          ElMessage.error('商品不存在或已被删除')
          router.push('/product/list')
        }
      } catch (error) {
        console.error('获取商品详情失败:', error)
        ElMessage.error('获取商品详情失败')
      } finally {
        loadingInstance.close()
      }
    }

    // 上传图片（封面图和图集）
    const uploadImage = async (file) => {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        ElMessage.error('只能上传图片文件')
        return null
      }
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        ElMessage.error('图片大小不能超过 5MB')
        return null
      }

      try {
        const signature = await getUploadSignature('image')
        const ossRes = await uploadToOSS(file, signature)
        return ossRes.url
      } catch (error) {
        console.error('上传失败:', error)
        // 显示具体的错误信息
        ElMessage.error(error.message || '上传失败')
        return null
      }
    }

    // 封面图上传
    const handleCoverChange = async (file) => {
      if (!file.raw) return
      const url = await uploadImage(file.raw)
      if (url) {
        formData.cover_image = url
        coverFiles.value = [{ uid: file.uid, name: file.name, url }]
        ElMessage.success('上传成功')
      }
    }

    // 商品图集上传
    const handleImagesChange = async (file, fileList) => {
      if (!file.raw) return
      const url = await uploadImage(file.raw)
      if (url) {
        formData.images.push(url)
        imageFiles.value = fileList.map((f, idx) => ({
          uid: f.uid,
          name: f.name,
          url: f.url || (formData.images[idx] || '')
        }))
        ElMessage.success('上传成功')
      }
    }

    const handleImagesRemove = (file) => {
      const index = formData.images.findIndex(img => img === file.url)
      if (index !== -1) {
        formData.images.splice(index, 1)
      }
      imageFiles.value = imageFiles.value.filter(f => f.uid !== file.uid)
    }

    // 获取商品规格列表
    const fetchSpecList = async () => {
      // 只有在编辑模式且有商品ID时才加载
      if (!productId.value || !isEditMode.value) {
        specList.value = []
        return
      }

      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/product_item_api.php`, {
          params: {
            agent_id: agentId.value,
            store_id: storeId.value,
            product_id: productId.value,
            limit: 100
          }
        })
        if (response.data.code === 200) {
          specList.value = response.data.data.list
        }
      } catch (error) {
        console.error('获取规格失败:', error)
      }
    }

    // 打开规格弹窗
    const openSpecDialog = () => {
      Object.assign(specForm, {
        id: null,
        item_name: '',
        item_type: 'dish',
        category: '',
        price: 0,
        quantity: 1,
        unit: '份',
        sort_order: 0
      })
      specDialogVisible.value = true
    }

    // 编辑规格
    const editSpec = (spec) => {
      Object.assign(specForm, {
        id: spec.id,
        item_name: spec.item_name,
        item_type: spec.item_type || 'dish',
        category: spec.category || '',
        price: spec.price,
        quantity: spec.quantity,
        unit: spec.unit || '份',
        sort_order: spec.sort_order || 0
      })
      specDialogVisible.value = true
    }

    // 保存规格
    const saveSpec = async () => {
      if (!specForm.item_name) {
        ElMessage.warning('请输入规格名称')
        return
      }

      // 新增模式且没有商品ID时，提示先保存商品
      if (!productId.value && !isEditMode.value) {
        ElMessage.warning('请先保存商品，再添加规格')
        return
      }

      try {
        const payload = {
          agent_id: agentId.value,
          store_id: storeId.value,
          product_id: productId.value,
          item_name: specForm.item_name,
          item_type: specForm.item_type,
          category: specForm.category,
          price: specForm.price,
          quantity: specForm.quantity,
          unit: specForm.unit,
          sort_order: specForm.sort_order
        }

        let response
        if (specForm.id) {
          payload.id = specForm.id
          response = await axios.put(`${config.BASE_URL}/shop/api/product_item_api.php`, payload)
        } else {
          response = await axios.post(`${config.BASE_URL}/shop/api/product_item_api.php`, payload)
        }

        if (response.data.code === 200) {
          ElMessage.success(specForm.id ? '更新成功' : '添加成功')
          specDialogVisible.value = false
          await fetchSpecList()
        } else {
          ElMessage.error(response.data.message || '操作失败')
        }
      } catch (error) {
        console.error('保存规格失败:', error)
        ElMessage.error('操作失败')
      }
    }

    // 删除规格
    const deleteSpec = async (id) => {
      try {
        const response = await axios.delete(`${config.BASE_URL}/shop/api/product_item_api.php`, {
          data: { id, store_id: storeId.value }
        })
        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          fetchSpecList()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('删除规格失败:', error)
        ElMessage.error('删除失败')
      }
    }


    // 提交表单
    const submitForm = async () => {
      if (!formRef.value) return

      await formRef.value.validate(async (valid) => {
        if (!valid) return

        submitting.value = true
        const loadingInstance = ElLoading.service({ text: '保存中...' })

        try {
          // 处理使用规则
          let useRules = {}
          if (formData.use_rules_text) {
            try {
              useRules = JSON.parse(formData.use_rules_text)
            } catch {
              useRules = { text: formData.use_rules_text }
            }
          }

          const payload = {
            agent_id: agentId.value,
            store_id: storeId.value,
            product_name: formData.product_name,
            sub_title: formData.sub_title,
            category_id: formData.category_id,
            category_name: formData.category_id ? categories.value.find(c => c.id === formData.category_id)?.category_name : null,
            product_no: formData.product_no || generateProductNo(),
            original_price: formData.original_price,
            sale_price: formData.sale_price,
            discount: formData.discount,
            stock: formData.stock,
            fictitious_sold: formData.fictitious_sold,
            cover_image: formData.cover_image,
            images: formData.images,
            service_features: formData.service_features,
            use_rules: useRules,
            description: formData.description,
            goods_details: formData.goods_details,
            valid_start_date: formData.valid_start_date,
            valid_end_date: formData.valid_end_date,
            status: formData.status,
            sort_order: formData.sort_order
          }

          let response
          if (isEditMode.value) {
            payload.id = productId.value
            response = await axios.put(`${config.BASE_URL}/shop/api/product_api.php`, payload)
          } else {
            response = await axios.post(`${config.BASE_URL}/shop/api/product_api.php`, payload)
            // 新增成功后，获取新商品的ID，变为编辑模式
            if (response.data.code === 200 && response.data.id) {
              isEditMode.value = true
              productId.value = response.data.id
              // 更新URL的query参数（可选，方便刷新）
              router.replace({ query: { id: response.data.id } })
              // 重新加载商品详情和规格
              await fetchProductDetail()
              ElMessage.success('商品创建成功，现在可以添加规格了')
              return
            }
          }

          if (response.data.code === 200) {
            ElMessage.success(isEditMode.value ? '更新成功' : '创建成功')
            // 编辑模式：留在当前页，不跳转
            if (!isEditMode.value) {
              setTimeout(() => {
                router.push('/product/list')
              }, 1500)
            }
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('提交失败:', error)
          ElMessage.error('操作失败，请稍后重试')
        } finally {
          submitting.value = false
          loadingInstance.close()
        }
      })
    }

    // 监听分类变化，自动更新分类名称
    watch(() => formData.category_id, (newVal) => {
      if (newVal) {
        const cat = categories.value.find(c => c.id === newVal)
        formData.category_name = cat ? cat.category_name : ''
      } else {
        formData.category_name = ''
      }
    })

    // 监听价格变化，自动计算折扣
    watch([() => formData.original_price, () => formData.sale_price], ([original, sale]) => {
      if (original > 0 && sale > 0) {
        const discount = (sale / original * 10).toFixed(1)
        formData.discount = parseFloat(discount)
      }
    })

    onMounted(() => {
      fetchCategories()

      // 从 query 参数获取 id（/product/edit?id=2）
      const id = route.query.id
      if (id) {
        isEditMode.value = true
        productId.value = parseInt(id)
        fetchProductDetail()  // 这个函数内部会调用 fetchSpecList
      } else {
        formData.product_no = generateProductNo()
        // 新增模式：规格列表为空，等保存成功后再刷新页面或提示先保存
      }
    })

    return {
      submitting,
      formRef,
      quillRef,
      isEditMode,
      categories,
      coverFiles,
      imageFiles,
      formData,
      formRules,
      editorOptions,
      serviceOptions,
      handleCoverChange,
      handleImagesChange,
      handleImagesRemove,
      submitForm,
      specList,
      specDialogVisible,
      specForm,
      openSpecDialog,
      editSpec,
      saveSpec,
      deleteSpec,
      fetchSpecList
    }
  }
}
</script>

<style scoped lang="scss">
.add-product {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }

    .header-actions {
      display: flex;
      gap: 12px;
    }
  }

  .form-container {
    background: #fff;
    border-radius: 16px;
    padding: 24px;
  }

  .form-section {
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 20px;
      padding-left: 12px;
      border-left: 3px solid #409eff;
    }
  }

  .upload-tip {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 8px;
  }

  .form-tip {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 4px;
  }

  // 规格管理样式
  .spec-management {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 12px;
    width: 100%;

    .spec-header {
      margin-bottom: 12px;
      text-align: right;
    }

    .spec-list {
      .spec-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 12px;
        background: #f5f7fa;
        border-radius: 8px;
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }

        .spec-info {
          display: flex;
          gap: 20px;
          align-items: center;

          .spec-name {
            font-weight: 500;
            min-width: 100px;
          }

          .spec-price {
            color: #f56c6c;
            font-weight: 500;
          }

          .spec-quantity {
            color: #8c8c8c;
          }
        }

        .spec-actions {
          display: flex;
          gap: 8px;
        }
      }
    }

    .spec-empty {
      text-align: center;
      padding: 20px;
      color: #8c8c8c;
      font-size: 13px;
    }
  }

  .editor-wrapper {
    width: 100%;

    :deep(.ql-toolbar) {
      border-radius: 8px 8px 0 0;
    }

    :deep(.ql-container) {
      border-radius: 0 0 8px 8px;
      min-height: 300px;
    }
  }
}
</style>